/**
 * Public type for the Hub space density (spacing) customization.
 *
 * Values mirror the backend `HelpCenterSpaceDensity` GraphQL enum exactly
 * (uppercase), so there is no case-mapping at the read/write boundaries.
 *
 * The chosen density will control the vertical rhythm
 * between every element on the hub page and, eventually, the spacing inside
 * elements.
 */
export type SpaceDensity = 'DEFAULT' | 'COMFORTABLE' | 'BREEZY';

/**
 * Ordered list of selectable densities, in the order they are shown in the
 * config panel control
 */
export const SPACE_DENSITIES: readonly SpaceDensity[] = ['DEFAULT', 'COMFORTABLE', 'BREEZY'];

/**
 * The density selected when the admin has made no choice and no value is
 * persisted on the backend.
 */
export const DEFAULT_SPACE_DENSITY: SpaceDensity = 'DEFAULT';

/**
 * The CSS custom property the hub sets to drive the vertical space density
 * between bespoke hub extensions. `HubRendererWrapper` (view mode) and the hub
 * editor container (edit mode) read this variable as top/bottom padding on each
 * extension; leaving it unset falls back to `0`, so this is a no-op for hubs
 * without a density selected.
 *
 */
export const EDITOR_BLOCK_SPACING_VAR = '--ak-editor-extension-block-spacing';

/**
 * Top/bottom padding applied to each bespoke hub extension for each density.
 *
 * Design spec:
 * - DEFAULT: `0` — no extra padding, so extensions keep their current editor
 *   spacing (a no-op).
 * - COMFORTABLE: 16px top and bottom.
 * - BREEZY: 24px top and bottom.
 */
export const SPACE_DENSITY_BLOCK_SPACING: Record<SpaceDensity, string> = {
	DEFAULT: '0',
	COMFORTABLE: '16px',
	BREEZY: '24px',
};

/**
 * Returns the editor block-spacing value for a given density.
 */
export const getBlockSpacingForDensity = (density: SpaceDensity): string =>
	SPACE_DENSITY_BLOCK_SPACING[density];

/**
 * The gap (in pixels) between cards/items rendered inside hub extensions
 * (e.g. JSM Help cards, links-menu items) for each density. Applied to both the
 * horizontal and vertical gaps of the card grid.
 *
 * This gap feeds the card grid's width/columns calculation, so it is a plain
 * number (not a CSS value).
 *
 * Design spec:
 * - DEFAULT: 16px — the original card grid gap (a no-op for existing hubs).
 * - COMFORTABLE: 16px.
 * - BREEZY: 24px.
 */
export const SPACE_DENSITY_CARD_GAP: Record<SpaceDensity, number> = {
	DEFAULT: 16,
	COMFORTABLE: 16,
	BREEZY: 24,
};

/**
 * Returns the horizontal card gap (px) for a given density.
 */
export const getCardGapForDensity = (density: SpaceDensity): number =>
	SPACE_DENSITY_CARD_GAP[density];
